import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Settings, Instagram, Youtube, Globe, Facebook, Music2 } from '@/ui/icons'
import { Button } from '@/ui/components'
import { UserImageUpload } from './UserImageUpload'
import { UserProfileEditModal } from './UserProfileEditModal'
import { UserProfile } from '@/shared/api/users'
import { UserProfileApi } from '@/shared/api/users'
import { getUserBannerUrl, getUserProfileUrl } from '@/shared/utils/userImages'
import { useAuth } from '@/modules/auth'
import { useSubscription } from '@/modules/subscriptions'

interface UserProfileHeaderProps {
  profile: UserProfile
  userName: string
  isOwnProfile: boolean
  onProfileUpdate: (profile: UserProfile) => void
}

export function UserProfileHeader({ profile, userName, isOwnProfile, onProfileUpdate }: UserProfileHeaderProps) {
  const { getAccessToken } = useAuth()
  const { isPro } = useSubscription()
  const [isUploadingBanner, setIsUploadingBanner] = useState(false)
  const [isUploadingProfile, setIsUploadingProfile] = useState(false)
  const [isDeletingBanner, setIsDeletingBanner] = useState(false)
  const [isDeletingProfile, setIsDeletingProfile] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const userProfileApi = new UserProfileApi(getAccessToken)

  const bannerUrl = getUserBannerUrl(profile.banner_image_path)
  const profileUrl = getUserProfileUrl(profile.profile_image_path)

  const handleBannerUpload = async (file: File) => {
    setIsUploadingBanner(true)
    try {
      const updated = await userProfileApi.uploadBannerImage(profile.user_id, file)
      onProfileUpdate(updated)
    } finally {
      setIsUploadingBanner(false)
    }
  }

  const handleProfileUpload = async (file: File) => {
    setIsUploadingProfile(true)
    try {
      const updated = await userProfileApi.uploadProfileImage(profile.user_id, file)
      onProfileUpdate(updated)
    } finally {
      setIsUploadingProfile(false)
    }
  }

  const handleBannerDelete = async () => {
    setIsDeletingBanner(true)
    try {
      await userProfileApi.deleteBannerImage(profile.user_id)
      const updated = await userProfileApi.getUserProfile(profile.user_id)
      onProfileUpdate(updated)
    } finally {
      setIsDeletingBanner(false)
    }
  }

  const handleProfileDelete = async () => {
    setIsDeletingProfile(true)
    try {
      await userProfileApi.deleteProfileImage(profile.user_id)
      const updated = await userProfileApi.getUserProfile(profile.user_id)
      onProfileUpdate(updated)
    } finally {
      setIsDeletingProfile(false)
    }
  }

  const socialLinks = [
    { icon: Instagram, url: profile.instagram_url, label: 'Instagram' },
    { icon: Youtube, url: profile.youtube_url, label: 'YouTube' },
    { icon: Music2, url: profile.tiktok_url, label: 'TikTok' },
    { icon: Facebook, url: profile.facebook_url, label: 'Facebook' },
    { icon: Globe, url: profile.website_url, label: 'Website' }
  ].filter(link => link.url)

  return (
    <div className="relative">
      {/* Banner Section */}
      <div className="relative w-full h-80 md:h-[500px] bg-adventure-200 overflow-hidden">
        {isOwnProfile ? (
          <UserImageUpload
            type="banner"
            currentImageUrl={bannerUrl}
            onUpload={handleBannerUpload}
            onDelete={bannerUrl ? handleBannerDelete : undefined}
            isUploading={isUploadingBanner}
            isDeleting={isDeletingBanner}
          />
        ) : bannerUrl ? (
          <img
            src={bannerUrl}
            alt="Profile banner"
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-adventure-100">
            <div className="text-center text-adventure-700">
              <p className="text-sm">No banner image</p>
            </div>
          </div>
        )}
        {/* Bottom gradient fade - smoother transition */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-[250px] bg-gradient-to-b from-transparent via-[hsl(var(--background))]/30 to-[hsl(var(--background))] dark:via-neutral-900/30 dark:to-neutral-900" />

        {/* Profile content overlaid on banner */}
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-6 z-20 pointer-events-none">
          <div className="pointer-events-auto relative z-30" style={{ pointerEvents: 'auto' }}>
            {isOwnProfile ? (
              <UserImageUpload
                type="profile"
                currentImageUrl={profileUrl}
                onUpload={handleProfileUpload}
                onDelete={profileUrl ? handleProfileDelete : undefined}
                isUploading={isUploadingProfile}
                isDeleting={isDeletingProfile}
              />
            ) : profileUrl ? (
              <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg overflow-hidden">
                <img
                  src={profileUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center">
                <span className="text-4xl font-bold text-gray-400">
                  {userName.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          <div className="text-center mt-4 relative z-20 pointer-events-auto">
            <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)]">
              {userName}
            </h1>
            {isOwnProfile && (
              <div className="mt-2">
                {isPro ? (
                  <span className="inline-block px-3 py-1 text-xs font-bold rounded-full bg-adventure-600/90 text-white backdrop-blur-sm shadow-sm">
                    Pro Member
                  </span>
                ) : (
                  <Link to="/pricing" className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-black/30 text-white/90 backdrop-blur-sm hover:bg-black/50 transition-colors shadow-sm">
                    Free Plan · Upgrade
                  </Link>
                )}
              </div>
            )}
            {profile.location && (
              <p className="text-lg md:text-xl text-white mt-2 drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] font-medium">
                {profile.location}
              </p>
            )}
            {profile.bio && (
              <p className="text-base md:text-lg text-white mt-4 max-w-2xl mx-auto drop-shadow-[0_2px_6px_rgba(0,0,0,0.6)] px-4 leading-relaxed">
                {profile.bio}
              </p>
            )}
            {/* Social Links */}
            {socialLinks.length > 0 && (
              <div className="flex items-center justify-center gap-3 mt-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm hover:bg-black/60 flex items-center justify-center transition-colors cursor-pointer shadow-lg border border-white/20"
                    aria-label={link.label}
                  >
                    <link.icon className="w-5 h-5 text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Buttons */}
      {isOwnProfile && (
        <div className="absolute top-4 right-4 z-40 flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsEditModalOpen(true)}
            className="bg-white/90 backdrop-blur-sm hover:bg-white text-gray-900 hover:text-gray-900"
          >
            <Settings className="w-4 h-4 mr-2" />
            Edit Profile
          </Button>
        </div>
      )}

      {/* Edit Modal */}
      {isOwnProfile && (
        <UserProfileEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          profile={profile}
          onProfileUpdated={onProfileUpdate}
        />
      )}
    </div>
  )
}

